import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link as NavLink, useNavigate, useSearchParams } from "react-router";

import * as Api from "@/api";
import InputPassword from "@/design/forms/InputPassword";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Sesame from "@/utils/Sesame";
import Valkyrie, {
  viArrowRight,
  viCheck,
  viShieldKeyhole,
  viSpinnerThird,
  viTriangleExclamation,
} from "@sippy-platform/valkyrie";

import { Button, Link, Stack, Typography } from "@mui/material";

import authReducer from "./authReducer";
import AuthUI from "./AuthUI";

export default function ConfirmAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const passwordRequired = searchParams.get("set_password") === "true";

  const auth = useSelector((state) => state.auth);

  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!searchParams.get("token")) {
      navigate("/login");
    }
  }, [navigate, searchParams]);

  const { isValid, value, dispatch, errors, save, pending } = useSave({
    initialValue: { password: "", confirmPassword: "" },
    mode: SaveMode.MANUAL,
    reducer: authReducer,
    validator: () => ({
      password: string()
        .required({ message: "Password is required." })
        .length([8], { message: "Password must be at least 8 characters." })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password should have at least one letter and one number.",
        })
        .truthy(value.confirmPassword === value.password, { message: "Passwords do not match." }),
    }),
    onSubmit: () => Api.putVerifyAccount(searchParams.get("token"), value),
    onSuccess: () => setSuccess(true),
  });

  useEffect(() => {
    if (!passwordRequired && !auth?.id) {
      save();
    }
  }, [auth?.id, passwordRequired, save]);

  function generatePassword() {
    const newPassword = new Sesame(16).generate();
    dispatch({ type: "resetPassword", value: newPassword });
    setShowPassword(true);
  }

  return (
    <AuthUI
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <title>{`Confirm account \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Typography variant="h4" sx={{ m: 0 }}>
        Verify account
      </Typography>
      {passwordRequired && !success ? (
        <>
          <Typography sx={{ mb: 2 }}>Set up your password to confirm your account.</Typography>
          <InputPassword
            required
            showPasswordStrength
            showPassword={showPassword}
            placeholder="Password"
            id="password"
            label="Password"
            value={value.password}
            onChange={(id, value) => dispatch({ type: id, value })}
            error={errors}
          />
          <InputPassword
            required
            placeholder="Confirm password"
            id="confirmPassword"
            label="Confirm password"
            value={value.confirmPassword}
            onChange={(id, value) => dispatch({ type: id, value })}
            error={errors}
          />
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
            }}
          >
            <Link
              onClick={generatePassword}
              component="button"
              type="button"
              color="inherit"
              underline="hover"
              variant="body2"
            >
              Generate password
            </Link>
            <Link component={NavLink} to="/login" color="inherit" underline="hover" variant="body2">
              Login
            </Link>
          </Stack>
          <Button
            variant="contained"
            size="large"
            type="submit"
            endIcon={<Valkyrie icon={pending ? viSpinnerThird : viShieldKeyhole} spin={pending} />}
            disabled={pending || !isValid}
          >
            Save password
          </Button>
        </>
      ) : (
        <>
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-start",
              gap: 1,
              mb: 2,
            }}
          >
            <Valkyrie
              icon={success ? viCheck : pending ? viSpinnerThird : viTriangleExclamation}
              spin={pending}
              style={{ marginTop: 6 }}
            />
            <Typography>
              {success
                ? "Account confirmed."
                : pending
                  ? "Confirming your account..."
                  : "We couldn't confirm this account. Check if the link hasn't expired already or the account has already been confirmed."}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            size="large"
            type="button"
            endIcon={<Valkyrie icon={viArrowRight} />}
            disabled={pending}
          >
            Continue
          </Button>
        </>
      )}
    </AuthUI>
  );
}
